<ng-template #helpPanel><f-and-i-help-panel></f-and-i-help-panel></ng-template>
<section class="content-area">
    <div class="right-panel">
        <div *ngIf="createClicked" class="instruction-container">
            <div class="instruction">
                <span>Complete all sections below to add a new form.</span>
            </div>
        </div>
        <div *ngIf="!createClicked" class="create-button-container">
            <button mat-button class="primary" (click)="createNew()">Create F&I Form</button>
        </div>

        <form [formGroup]="documentForm" (ngSubmit)="saveForm()">
            <div class="read-only-text" *ngIf="userIsInternal() && isCommonDoc()">
                *The document selected is a Common Document shared with multiple dealerships and is not editable from this context. Please edit the document
                from the Document Library Admin page.
            </div>

            <div class="read-only-text" *ngIf="(!this.userIsInternal() && this.isSystemDoc()) || (!userIsInternal() && isCommonDoc())">
                *This is a system document that cannot be modified or deleted.
            </div>

            <div *ngIf="userIsInternal()" class="card">
                <h3 class="top-of-form">Please select an internal document type</h3>
                <mat-divider></mat-divider>
                <mat-card-content>
                    <app-internal-document-type
                        id="document-type-selection"
                        [types]="types"
                        [internalDocumentTypeSelected]="internalDocumentTypeSelected"
                        (internalDocumentTypeChange)="internalDocumentSelectedChanged($event)"
                        [internalDocumentNotSelected]="internalDocumentNotSelected"
                        [disabled]="isCommonDoc() || (isSystemDoc() && !userIsInternal())"
                    >
                    </app-internal-document-type>
                </mat-card-content>
            </div>

            <div class="card">
                <h3 class="with-info">
                    Form Information
                    <mat-icon
                        class="info-icon"
                        matTooltip="Add or edit the form name and a description. Both are required and will be viewable by customers, so be sure it is easy for anyone to recognize the form and its purpose. Since many customers may use their mobile devices to access forms, keep both the name and description as short as possible."
                        >info_flat</mat-icon
                    >
                </h3>
                <mat-divider></mat-divider>
                <mat-card-content>
                    <div>
                        <p *ngIf="!addFormToDealMode()">*Indicates a required field</p>
                        <br *ngIf="addFormToDealMode()" />
                        <mat-form-field class="col">
                            <mat-label>Name (limited to 100 characters)</mat-label>
                            <input type="text" matInput formControlName="name" maxlength="100" />
                            <mat-error>Name is required</mat-error>
                        </mat-form-field>
                    </div>
                    <div>
                        <mat-form-field class="col">
                            <mat-label>Description</mat-label>
                            <textarea matInput formControlName="preamble" rows="4" maxlength="2000"></textarea>
                            <mat-error>Description is required</mat-error>
                        </mat-form-field>
                    </div>
                </mat-card-content>
            </div>

            <div class="card">
                <h3 class="top-of-form with-info">
                    Form Type
                    <mat-icon
                        class="info-icon"
                        matTooltip="Select the type of form and then upload a template of the form as needed to add signatures, initials and other fields. For additional information, open the When to Use dropdown panel. NOTE: Forms that are printed with data from your DMS and other systems require enrollment in an enhanced service package."
                        >info_flat</mat-icon
                    >
                </h3>
                <mat-divider></mat-divider>
                <mat-card-content>
                    <div class="upload-card-body">
                        <mat-radio-group formControlName="typeOfDocument" class="row">
                            <div *ngFor="let type of templateUploadType" class="radio-button col-sm-6">
                                <div
                                    [matTooltip]="
                                        type.uploadTypeDescription === 'Print From Pdf' && documentForm.controls.fileProvidedBy.disabled
                                            ? 'Forms that are printed with data from your DMS and other systems require enrollment in an enhanced service package. '
                                            : type.uploadTypeDescription === 'Print From Impact Printer' && documentForm.controls.fileProvidedBy.disabled
                                              ? 'Forms that are printed with data from your DMS and other systems require enrollment in an enhanced service package. '
                                              : ''
                                    "
                                >
                                    <mat-radio-button
                                        (change)="radioChange($event)"
                                        [value]="type.uploadTypeId"
                                        [checked]=""
                                        [disabled]="(type.uploadTypeId === 4 || type.uploadTypeId === 3) && !(userIsInternal() || dealerHasPrintToDdj())"
                                        matTooltipPosition="above"
                                        [matTooltip]="type.hint ? type.hint : type.uploadTypeDescription ?? getFormName(type.uploadTypeId)"
                                    >
                                        {{ getFormName(type.uploadTypeId) }}
                                    </mat-radio-button>
                                </div>
                            </div>
                        </mat-radio-group>

                        <ng-container *ngIf="documentForm.get('typeOfDocument').value !== 2 && documentForm.get('typeOfDocument').value !== 7">
                            <div class="file-buttons">
                                <div (click)="uploadFile.click()" id="file-selection-button">
                                    <button *ngIf="showFileSelection()" mat-button type="button" class="file-selection">
                                        {{ fileName || 'Choose File' }}
                                    </button>
                                    <input
                                        type="file"
                                        #uploadFile
                                        (change)="selectPdfForEditing($event.target['files'][0])"
                                        (click)="openFileChooser($event)"
                                        accept=".pdf"
                                    />
                                </div>
                                <ng-container *ngIf="showFileSelection()">
                                    <div>
                                        <button mat-button id="cancel-upload" type="button" (click)="cancelUpload()">Remove</button>
                                    </div>
                                </ng-container>
                            </div>
                        </ng-container>
                        <mat-error *ngIf="formErrors.uploadFile">{{ formErrors.uploadFile }}</mat-error>
                    </div>
                </mat-card-content>

                <!--Dropdown Help Expansion Panel-->
                <mat-expansion-panel *ngIf="selectedFormType === 'predefined-form'" #formsPanel hideToggle="true" class="mat-expansion-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title class="mat-expansion-panel-header-title">
                            When to Use Form Ready For Signatures
                            <mat-icon *ngIf="!formsPanel.expanded" class="question"> help </mat-icon>
                            <mat-icon *ngIf="formsPanel.expanded" class="expanded-question"> help </mat-icon>
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <mat-panel-description class="panel-desc">
                        <p>
                            Forms ready for signature are documents that do not require any data to be printed from your DMS or other systems. Data fields like
                            name, legal name and date can be added to the document, in addition to signatures and initials.
                        </p>
                        <p>
                            A system form that is an example of this type of document is the e-sign consent forms for buyers and co-buyers. Other examples
                            include documents confirming a customer was given a vehicle walk-through and a Carfax report on a used vehicle (when you want a
                            customer to acknowledge receipt).
                        </p>
                        <p>These documents are only added by dealer users. They must be uploaded as PDFs and can be no larger than 25 MB.</p>
                    </mat-panel-description>
                </mat-expansion-panel>

                <mat-expansion-panel *ngIf="selectedFormType === 'upload-during-deal'" #formsPanel hideToggle="true" class="mat-expansion-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title class="mat-expansion-panel-header-title">
                            When to Use Manually Uploaded During the Deal
                            <mat-icon *ngIf="!formsPanel.expanded" class="question"> help </mat-icon>
                            <mat-icon *ngIf="formsPanel.expanded" class="expanded-question"> help </mat-icon>
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <mat-panel-description class="panel-desc">
                        <p>Manually uploaded documents are those that do not require any data or any signatures or initials.</p>
                        <p>
                            A system form that is an example of this type of document is a driver’s license. Other examples include documents you want to add to
                            a Deal Jacket for reference only, like a Carfax report on a used vehicle (when you do not need a customer to acknowledge receipt)
                            and a copy of the window sticker. Examples of documents customers will add include proof of insurance and bank statements.
                        </p>
                        <p>
                            These documents can be added by buyers and co-buyers, as well as dealer users. Accepted file types include PDF, JPG and PNG. Maximum
                            file size is 100MB.
                        </p>
                        <p><b>NOTE:</b> If you need a document to be uploaded by both a buyer and a co-buyer, create two versions of the form.</p>
                    </mat-panel-description>
                </mat-expansion-panel>

                <mat-expansion-panel *ngIf="selectedFormType === 'print-from-pdf'" #formsPanel hideToggle="true" class="mat-expansion-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title class="mat-expansion-panel-header-title">
                            When to Use Print From Laser Form:
                            <mat-icon *ngIf="!formsPanel.expanded" class="question"> help </mat-icon>
                            <mat-icon *ngIf="formsPanel.expanded" class="expanded-question"> help </mat-icon>
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <mat-panel-description class="panel-desc">
                        <p>
                            Select this form type for Deal Jacket documents that are set up in your DMS and other systems to print to as laser forms (i.e.,
                            everything is printed, including the form layout). Additional data fields like name, legal name and date can be added to the
                            document, in addition to signatures and initials.
                        </p>
                        <p>These documents are only added by dealer users. They must be uploaded as PDFs and can be no larger than 25 MB.</p>
                        <p>
                            If you have a laser form to be added or modified and need help, contact us at {{ tenantSupportEmail }} or
                            {{ tenantSupportPhone }} for assistance.
                        </p>
                    </mat-panel-description>
                </mat-expansion-panel>

                <mat-expansion-panel *ngIf="selectedFormType === 'print-from-impact'" #formsPanel hideToggle="true" class="mat-expansion-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title class="mat-expansion-panel-header-title">
                            When to Use Print From Impact Form:
                            <mat-icon *ngIf="!formsPanel.expanded" class="question"> help </mat-icon>
                            <mat-icon *ngIf="formsPanel.expanded" class="expanded-question"> help </mat-icon>
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <mat-panel-description class="panel-desc">
                        <p>
                            Select this form type for Deal Jacket documents that are set up in your DMS and other systems to print on impact printers (i.e., the
                            data is printed on form that already includes the form layout). Additional data fields like name, legal name and date can be added
                            to the document, in addition to signatures and initials.
                        </p>
                        <p>These documents are only added by dealer users. They must be uploaded as PDFs and can be no larger than 25 MB.</p>
                        <p>
                            If you have a laser form to be added or modified and need help, contact us at {{ tenantSupportEmail }} or
                            {{ tenantSupportPhone }} for assistance.
                        </p>
                    </mat-panel-description>
                </mat-expansion-panel>

                <mat-expansion-panel *ngIf="selectedFormType === 'manually-filled-during-deal'" #formsPanel hideToggle="true" class="mat-expansion-panel">
                    <mat-expansion-panel-header>
                        <mat-panel-title class="mat-expansion-panel-header-title">
                            When to Use Manually Filled During Deal:
                            <mat-icon *ngIf="!formsPanel.expanded" class="question"> help </mat-icon>
                            <mat-icon *ngIf="formsPanel.expanded" class="expanded-question"> help </mat-icon>
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <mat-panel-description class="panel-desc">
                        <p>Some information here about what the form does.</p>
                        <p>
                            These documents are only added by dealer users and will first go to dealer queue. They must be uploaded as PDFs and can be no larger
                            than 25 MB.
                        </p>
                        <p>
                            If you have a Manually Filled During Deal form to be added or modified and need help, contact us at {{ tenantSupportEmail }} or
                            {{ tenantSupportPhone }} for assistance.
                        </p>
                    </mat-panel-description>
                </mat-expansion-panel>
                <!--Panel END-->
            </div>
            <div class="card">
                <h3 class="with-info">
                    Form Will Be Added By:
                    <mat-icon
                        class="info-icon"
                        matTooltip="Select the type of form and then upload a template of the form as needed to add signatures, initials and other fields. For additional information, open the When to Use dropdown panel. NOTE: Forms that are printed with data from your DMS and other systems require enrollment in an enhanced service package."
                        >info_flat</mat-icon
                    >
                </h3>
                <mat-divider></mat-divider>
                <mat-card-content>
                    <div class="form-group" id="user-selection-radio">
                        <mat-radio-group formControlName="fileProvidedBy" class="provided-by">
                            <mat-radio-button class="radio-button" [value]="assignees.buyer">Buyer</mat-radio-button>
                            <mat-radio-button class="radio-button" [value]="assignees.cobuyer">Co-Buyer</mat-radio-button>
                            <mat-radio-button class="radio-button" [value]="assignees.dealer">Dealer</mat-radio-button>
                        </mat-radio-group>
                    </div>
                </mat-card-content>
            </div>

            <div class="standalone-checkboxes">
                <div class="form-group" *ngIf="(this.documentForm.get('fileProvidedBy').value === assignees.dealer && userIsInternal()) || userIsDealer()">
                    <label>
                        <mat-checkbox formControlName="isDealerOnly"> This document will only be seen by the Dealer </mat-checkbox>
                    </label>
                </div>
            </div>
            <div class="form-buttons" *ngIf="(!isCommonDoc() && (!isSystemDoc() || userIsInternal())) || addFormToDealMode()">
                <button mat-button type="button" class="secondary" (click)="createNew(false)">Cancel</button>
                <ng-container *ngIf="isDeleteAllowed && !addFormToDealMode()">
                    <button mat-button type="button" class="delete" (click)="deleteTemplate()">Delete</button>
                </ng-container>
                <button mat-button class="primary" type="submit" [disabled]="errorState">Save</button>
            </div>
        </form>
        <ng-container *ngIf="pdfDataSet">
            <pdf-view
                [template]="title"
                [docId]="docId"
                [document]="selectedPdf"
                [fields]="pdfFields"
                [emitHasDocumentUrl]="selectedPdfHasDocUrl"
                [allowPdfTronDownload]="true"
                [commonDocument]="selectedPdf && selectedPdf.commonDocument"
                (outEvent)="eventHandler($event)"
            >
            </pdf-view>
        </ng-container>
    </div>

    <div class="left-panel">
        <div class="card">
            <div class="left-panel-title with-info">
                {{ !addFormToDealMode() ? 'Select Existing ' : 'Add ' }} F&I Form
                <mat-icon
                    class="info-icon"
                    matTooltip="Select a form from the list below to view or modify it. NOTE: Changes to existing forms will not impact any existing Deal Jackets that include the form. They will only be applied to Deal Jackets created after the form has been updated. Deleting a form will not remove it from any existing Deal Jackets."
                    >info_flat</mat-icon
                >
            </div>

            <ng-container *ngIf="dealDocsCore.length <= 0">
                <mat-list *ngIf="coreForms.length >= 1; else noForms">
                    <mat-list-item
                        [ngClass]="form.documentId === docId ? 'list-item-selected' : 'list-item'"
                        matTooltipPosition="left"
                        *ngFor="let form of coreForms | activeInternalDocumentType"
                        (click)="editForm(form)"
                    >
                        <p [ngClass]="form.commonDocument || (form.systemDocument && !userIsInternal()) ? 'list-item-text-alt' : 'list-item-text'">
                            {{ form.displayName }}
                        </p>
                    </mat-list-item>
                </mat-list>
            </ng-container>
            <ng-container *ngIf="dealDocsCore.length >= 1">
                <mat-list *ngIf="coreForms.length >= 1; else noForms">
                    <mat-list-item
                        *ngFor="let form of coreForms | activeInternalDocumentType"
                        (click)="selectForm(form)"
                        [class.selected]="form.documentId === docId"
                        [disabled]="isPreventCopy(form)"
                    >
                        <p class="list-item-text">{{ form.displayName }}</p>
                        <mat-chip-list *ngIf="!isPreventCopy(form)">
                            <mat-chip>
                                <mat-icon>add</mat-icon>
                            </mat-chip>
                        </mat-chip-list>
                    </mat-list-item>
                </mat-list>
            </ng-container>
        </div>
        <ng-template #noForms>
            <p class="no-forms">No forms created yet</p>
        </ng-template>
    </div>
</section>
<ddj-spinner *ngIf="isLoading"></ddj-spinner>
